const SIZE_LABELS = new Map([
  ['M', 'M / 38'],
  ['L', 'L / 40'],
  ['XL', 'XL / 42'],
  ['XXL', 'XXL / 44'],
]);

class CaddyPdpController {
  constructor(section) {
    this.section = section;
    this.intentionalSize = new URL(window.location.href).searchParams.has('variant');
    this.buyNowSizeChosen = false;
    this.initialViewportHeight = window.visualViewport?.height ?? window.innerHeight;
    this.abortController = new AbortController();
    this.resizeObserver = new ResizeObserver(() => this.updateStickyEligibility());
    this.boundTracks = new WeakSet();
    this.galleryIndex = 0;
    this.mutationObserver = new MutationObserver(() => this.reconcile());
    this.setup();
  }

  setup() {
    const { signal } = this.abortController;
    this.refreshElements();

    if (!this.intentionalSize) this.clearImplicitSize();
    this.updateStickyCopy();
    this.setupGallery();
    this.updateStickyEligibility();
    this.updateOverlayState();
    this.updateMobileSticky();

    this.section.addEventListener('purchase-state-change', () => {
      this.updateBuyNow();
      this.updateStickyCopy(this.sizeFieldset?.querySelector('input:checked')?.value);
    }, { signal });
    this.section.addEventListener('change', (event) => this.handleChange(event), { signal });
    this.section.addEventListener('click', (event) => {
      // Clicking an already checked variant-linked size is still an explicit choice.
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-caddy-size-option]') && event.target.dataset.optionAvailable === 'true') {
        this.buyNowSizeChosen = true;
        this.hideSizeError();
      }
    }, { signal });
    document.addEventListener('click', (event) => this.openSizeGuideProxy(event), { signal });
    document.addEventListener('click', (event) => this.guardPurchase(event), { capture: true, signal });
    this.section.addEventListener('submit', (event) => this.guardSubmit(event), { capture: true, signal });
    window.addEventListener('resize', () => {
      this.updateStickyEligibility();
      this.updateMobileSticky();
    }, { signal });
    document.addEventListener('scroll', () => this.updateMobileSticky(), { capture: true, passive: true, signal });
    window.visualViewport?.addEventListener('resize', () => this.updateKeyboardState(), { signal });
    this.resizeObserver.observe(this.details);
    this.mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['open', 'aria-hidden'],
    });
  }

  refreshElements() {
    this.sizeFieldset = this.section.querySelector('.caddy-pdp-size');
    this.sizeError = this.section.querySelector('[data-caddy-size-error]');
    this.buyNow = this.section.querySelector('.product-information [data-caddy-buy-now]');
    this.updateBuyNow();
    this.details = this.section.querySelector('.product-details');
    this.sticky = this.section.querySelector('.caddy-pdp-sticky-atb');
  }

  clearImplicitSize() {
    this.sizeFieldset?.querySelectorAll('[data-caddy-size-option]').forEach((input) => {
      input.checked = false;
      input.dataset.currentChecked = 'false';
    });
    this.sizeFieldset?.setAttribute('aria-invalid', 'false');
  }

  handleChange(event) {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input?.matches('[data-caddy-size-option]')) return;

    this.intentionalSize = true;
    this.buyNowSizeChosen = true;
    this.hideSizeError();
    this.updateStickyCopy(input.value);
    this.updateBuyNow();
  }

  openSizeGuideProxy(event) {
    const proxy = event.target instanceof Element ? event.target.closest('[data-caddy-size-guide-proxy]') : null;
    if (!proxy) return;
    const trigger = this.section.querySelector('[data-caddy-size-guide-open]');
    if (trigger instanceof HTMLButtonElement) trigger.click();
  }

  guardPurchase(event) {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target) return;
    if (this.section.contains(target) && target.matches('[data-caddy-buy-now]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.handleBuyNow();
      return;
    }

    const isPrimary = target.closest('.product-information') && target.matches('[ref="addToCartButton"]');
    const isSticky = target.closest('.caddy-pdp-sticky-atb') && target.matches('[ref="addToCartButton"]');
    if (!isPrimary && !isSticky) return;
    if (this.intentionalSize || !this.sizeFieldset) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showSizeError();
  }

  guardSubmit(event) {
    if (this.buyNowBusy || this.purchaseForm?.purchaseInFlight) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (this.intentionalSize || !this.sizeFieldset) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.showSizeError();
  }

  get purchaseForm() {
    return this.section.querySelector('.product-information product-form-component');
  }

  updateBuyNow() {
    if (!this.buyNow) return;
    const selected = this.sizeFieldset?.querySelector('[data-caddy-size-option]:checked');
    const unavailable = selected?.dataset.optionAvailable === 'false';
    const primary = this.section.querySelector('.product-information [ref="addToCartButton"]');
    this.buyNow.disabled = Boolean(this.buyNowBusy || this.purchaseForm?.purchaseInFlight || unavailable || this.buyNow.dataset.productAvailable === 'false' || (!this.sizeFieldset && primary?.disabled));
    if (this.buyNowBusy && this.sizeFieldset) this.sizeFieldset.inert = true;
    this.buyNow.setAttribute('aria-disabled', String(this.buyNow.disabled));
    this.buyNow.setAttribute('aria-busy', String(Boolean(this.buyNowBusy || this.purchaseForm?.purchaseInFlight)));
  }

  async handleBuyNow() {
    if (this.buyNowBusy || this.purchaseForm?.purchaseInFlight) return;
    const selected = this.sizeFieldset?.querySelector('[data-caddy-size-option]:checked');
    if (this.sizeFieldset && (!this.buyNowSizeChosen || !selected?.dataset.variantId)) {
      this.showSizeError();
      return;
    }
    if (selected && (selected.disabled || selected.dataset.optionAvailable !== 'true')) return;
    const form = this.buyNow.closest('form');
    if (!form || !form.reportValidity()) return;
    // Capture the clicked option, never the potentially stale first-variant hidden input.
    const body = new FormData(form);
    const variantId = selected?.dataset.variantId || String(body.get('id') || '');
    if (!variantId) return;
    body.set('id', variantId);
    const quantity = Number(body.get('quantity') || 1);
    const error = this.section.querySelector('[data-caddy-buy-now-error]');
    if (error) error.hidden = true;
    const fieldset = this.sizeFieldset;
    const wasInert = fieldset?.inert;
    const purchaseForm = this.purchaseForm;
    if (!purchaseForm?.acquirePurchase()) return;
    let navigating = false;
    this.buyNowBusy = true;
    this.updateBuyNow();
    if (fieldset) fieldset.inert = true;
    const root = window.Shopify?.routes?.root || '/';
    try {
      const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Unable to check your bag. Please try again.');
      const cart = await cartResponse.json();
      if (this.sizeFieldset?.querySelector('[data-caddy-size-option]:checked')?.dataset.variantId !== variantId && this.sizeFieldset) throw new Error('Your size changed. Please choose Buy Now again.');
      const properties = Object.fromEntries([...body].filter(([key]) => key.startsWith('properties[')).map(([key, value]) => [key.slice(11, -1), value]).filter(([, value]) => value !== ''));
      const existing = cart.items.find((item) => String(item.variant_id) === variantId && String(item.selling_plan_allocation?.selling_plan?.id || '') === String(body.get('selling_plan') || '') && JSON.stringify(Object.entries(item.properties || {}).sort()) === JSON.stringify(Object.entries(properties).sort()) && item.quantity >= quantity);
      if (!existing) {
        // Shopify's native Ajax endpoint remains the inventory/quantity authority.
        if (!body.has('quantity')) body.set('quantity', '1');
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this size. Please try again.');
      }
      // No cart-update event: Add to Bag alone owns drawer opening.
      window.location.assign(`${root}checkout`);
      navigating = true;
    } catch (failure) {
      if (error) { error.textContent = failure.message || 'Unable to continue to checkout. Please try again.'; error.hidden = false; }
    } finally {
      if (fieldset) fieldset.inert = wasInert;
      if (this.sizeFieldset) this.sizeFieldset.inert = wasInert;
      if (!navigating) {
        this.buyNowBusy = false;
        purchaseForm.releasePurchase();
        this.updateBuyNow();
      }
    }
  }

  showSizeError() {
    this.sizeFieldset.setAttribute('aria-invalid', 'true');
    if (this.sizeError) this.sizeError.hidden = false;

    const legend = this.sizeFieldset.querySelector('legend');
    if (legend instanceof HTMLElement) {
      legend.tabIndex = -1;
      legend.focus({ preventScroll: true });
      legend.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' });
    }
  }

  hideSizeError() {
    this.sizeFieldset?.setAttribute('aria-invalid', 'false');
    if (this.sizeError) this.sizeError.hidden = true;
  }

  updateStickyCopy(value) {
    const label = this.sticky?.querySelector('.sticky-add-to-cart__variant');
    if (!label) return;
    const text = value ? SIZE_LABELS.get(value) ?? value : 'Choose size';
    if (label.textContent !== text) label.textContent = text;
    const selected = this.intentionalSize && this.sizeFieldset?.querySelector('[data-caddy-size-option]:checked');
    const button = this.sticky.querySelector('[ref="addToCartButton"]');
    const action = button?.querySelector('.add-to-cart-text__content > span');
    const primary = this.section.querySelector('.product-information [ref="addToCartButton"]');
    const nextText = !selected ? 'Select size' : selected.dataset.optionAvailable === 'true' ? 'Add to bag' : 'Sold out';
    if (action && action.textContent !== nextText) action.textContent = nextText;
    // Keep the native form as availability and pending-variant authority.
    if (button) {
      button.disabled = Boolean(this.purchaseForm?.purchaseInFlight || (selected ? primary?.disabled || selected.dataset.optionAvailable !== 'true' : false));
      button.setAttribute('aria-disabled', String(button.disabled));
      button.setAttribute('aria-busy', String(Boolean(this.purchaseForm?.purchaseInFlight)));
    }
  }

  setupGallery() {
    const gallery = this.section.querySelector('.caddy-pdp-gallery');
    const track = gallery?.querySelector('[data-caddy-gallery-track]');
    if (!(track instanceof HTMLElement) || this.boundTracks.has(track)) return;
    this.boundTracks.add(track);

    const slides = [...track.children].filter((slide) => slide instanceof HTMLElement);
    const current = gallery.querySelector('[data-caddy-gallery-current]');
    const previous = gallery.querySelector('[data-caddy-gallery-previous]');
    const next = gallery.querySelector('[data-caddy-gallery-next]');
    let activeIndex = 0;

    const update = (index) => {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1));
      this.galleryIndex = activeIndex;
      if (current) current.textContent = String(activeIndex + 1);
      previous?.toggleAttribute('disabled', activeIndex === 0);
      next?.toggleAttribute('disabled', activeIndex === slides.length - 1);
    };

    const goTo = (index) => {
      const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!(slide instanceof HTMLElement)) return;
      track.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      update(index);
    };

    previous?.addEventListener('click', () => goTo(activeIndex - 1), { signal: this.abortController.signal });
    next?.addEventListener('click', () => goTo(activeIndex + 1), { signal: this.abortController.signal });
    track.addEventListener(
      'scroll',
      () => {
        const width = track.clientWidth || 1;
        update(Math.round(track.scrollLeft / width));
      },
      { passive: true, signal: this.abortController.signal }
    );
    track.tabIndex = 0;
    track.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(activeIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(activeIndex + 1);
        }
      },
      { signal: this.abortController.signal }
    );
    if (innerWidth < 1100) goTo(this.galleryIndex);
    else update(this.galleryIndex);
  }

  reconcile() {
    this.refreshElements();
    this.setupGallery();
    this.updateStickyCopy(this.sizeFieldset?.querySelector('input:checked')?.value);
    this.updateStickyEligibility();
    this.updateOverlayState();
    this.updateMobileSticky();
  }

  updateMobileSticky() {
    const button = this.section.querySelector('.product-information [ref="addToCartButton"]');
    if (!button || !this.sticky) return;
    const headerBottom = document.querySelector('#header-component')?.getBoundingClientRect().bottom ?? 0;
    const footer = document.querySelector('.cc-footer, footer');
    const footerVisible = footer && footer.getBoundingClientRect().top < innerHeight;
    const overlayOpen = document.body.classList.contains('caddy-pdp-overlay-open');
    const buttonRect = button.getBoundingClientRect();
    const isTablet = innerWidth >= 768 && innerWidth < 1100;
    const visiblePixels = Math.max(
      0,
      Math.min(buttonRect.bottom, innerHeight) - Math.max(buttonRect.top, headerBottom)
    );
    const mainPurchaseVisible = visiblePixels >= Math.min(32, buttonRect.height / 2);
    const purchaseOffscreen = isTablet
      ? !mainPurchaseVisible
      : buttonRect.bottom <= Math.max(0, headerBottom);
    const visible = innerWidth < 1100 && !footerVisible && !overlayOpen && purchaseOffscreen;
    this.sticky.classList.toggle('caddy-pdp-sticky-visible', visible);
    const bar = this.sticky.querySelector('.sticky-add-to-cart__bar');
    if (bar) bar.inert = !visible;
  }

  updateStickyEligibility() {
    if (!this.details) return;
    const availableHeight = window.innerHeight - 118;
    this.details.classList.toggle('caddy-pdp-sticky-disabled', this.details.scrollHeight > availableHeight);
  }

  updateOverlayState() {
    const overlayOpen = Boolean(document.querySelector('dialog[open], [role="dialog"][aria-hidden="false"]'));
    document.body.classList.toggle('caddy-pdp-overlay-open', overlayOpen);
  }

  updateKeyboardState() {
    const currentHeight = window.visualViewport?.height ?? window.innerHeight;
    document.body.classList.toggle('caddy-pdp-keyboard-open', currentHeight < this.initialViewportHeight * 0.72);
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

const caddyPdpControllers = new Map();
const initializeCaddyPdp = () => {
  for (const [section, controller] of caddyPdpControllers) {
    if (section.isConnected) continue;
    controller.abortController.abort();
    controller.resizeObserver.disconnect();
    controller.mutationObserver.disconnect();
    caddyPdpControllers.delete(section);
  }
  document.querySelectorAll('.shopify-section:has(.product-information)').forEach((section) => {
    if (!(section instanceof HTMLElement) || section.dataset.caddyPdpReady === 'true') return;
    section.dataset.caddyPdpReady = 'true';
    caddyPdpControllers.set(section, new CaddyPdpController(section));
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCaddyPdp, { once: true });
} else {
  initializeCaddyPdp();
}

document.addEventListener('shopify:section:load', initializeCaddyPdp);
